import React from 'react';
import { CheckCircle, AlertCircle, Eye } from 'lucide-react';
import { Button } from './FormComponents';
import './CourseCreationTabs.css';

export interface ReviewTabProps {
  // All form data for display
  courseType: 'web' | 'ilt' | 'blended';
  courseTitle: string;
  instructorName: string;
  shortDescription: string;
  description: string;
  categoryName: string;
  subcategoryName: string;
  languageName: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  courseDuration: string;
  tags: string[];
  requirements: string[];
  outcomes: string[];
  faqCount: number;
  thumbnailFile: File | null;
  modulesCount: number;
  offeringsCount: number;
  sessionsCount: number;
  visibilityType: 'public' | 'restricted';

  // Actions
  isCreating: boolean;
  onCreateCourse: () => void;
  isUpdate?: boolean; // Whether this is updating an existing course

  // Validation
  validationErrors: Record<string, string>;
  error: string | null;
  success: string | null;
}

export const ReviewTab: React.FC<ReviewTabProps> = ({
  courseType,
  courseTitle,
  instructorName,
  shortDescription,
  description,
  categoryName,
  subcategoryName,
  languageName,
  level,
  courseDuration,
  tags,
  requirements,
  outcomes,
  faqCount,
  thumbnailFile,
  modulesCount,
  offeringsCount,
  sessionsCount,
  visibilityType,
  isCreating,
  onCreateCourse,
  isUpdate = false,
  validationErrors,
  error,
  success,
}) => {
  const hasErrors = Object.keys(validationErrors).length > 0;
  const getCourseTypeLabel = () => {
    switch (courseType) {
      case 'web':
        return 'Web-based Course';
      case 'ilt':
        return 'ILT Course';
      case 'blended':
        return 'Blended Course';
      default:
        return courseType;
    }
  };

  return (
    <div className="tab-content">
      {/* Section Header */}
      <div className="section-header review-header">
        <h3>
          <CheckCircle size={20} />
          Review & Publish
        </h3>
        <p>Review your course details before publishing</p>
      </div>

      {/* Validation Messages */}
      {hasErrors && (
        <div className="alert alert-warning">
          <AlertCircle size={20} />
          <div>
            <strong>Please fix the following issues before publishing:</strong>
            <ul>
              {Object.entries(validationErrors).map(([key, message]) => (
                <li key={key}>{message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <div>
            <strong>Error</strong>
            <p>{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          <div>
            <strong>Success!</strong>
            <p>{success}</p>
          </div>
        </div>
      )}

      {/* Course Summary */}
      <div className="summary-grid">
        {/* Left Column - Course Preview */}
        <div className="preview-section">
          <h4>
            <Eye size={18} />
            Course Preview
          </h4>
          <div className="course-card">
            <div className="course-thumbnail">
              {thumbnailFile ? (
                <img
                  src={URL.createObjectURL(thumbnailFile)}
                  alt={courseTitle}
                  className="thumbnail-image"
                />
              ) : (
                <div className="thumbnail-placeholder">No Thumbnail</div>
              )}
            </div>
            <div className="course-info">
              <div className="course-type-badge">{getCourseTypeLabel()}</div>
              <h5>{courseTitle || 'Course Title'}</h5>
              <p className="course-instructor">by {instructorName}</p>
              <p className="course-description">
                {shortDescription || description || 'No description provided'}
              </p>
              <div className="course-meta">
                <span className="meta-item">{categoryName || 'Category'}</span>
                <span className="meta-divider">•</span>
                <span className="meta-item">{languageName || 'Language'}</span>
                <span className="meta-divider">•</span>
                <span className="meta-item">{level}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Details Summary */}
        <div className="details-section">
          <h4>Course Details Summary</h4>

          <div className="summary-group">
            <label>Basic Information</label>
            <div className="summary-items">
              <div className="summary-item">
                <span className="item-label">Type:</span>
                <span className="item-value">{getCourseTypeLabel()}</span>
              </div>
              <div className="summary-item">
                <span className="item-label">Category:</span>
                <span className="item-value">{categoryName || 'Not set'} {subcategoryName && `→ ${subcategoryName}`}</span>
              </div>
              <div className="summary-item">
                <span className="item-label">Language:</span>
                <span className="item-value">{languageName || 'Not set'}</span>
              </div>
              <div className="summary-item">
                <span className="item-label">Level:</span>
                <span className="item-value">{level}</span>
              </div>
              {courseDuration && (
                <div className="summary-item">
                  <span className="item-label">Duration:</span>
                  <span className="item-value">{courseDuration}</span>
                </div>
              )}
            </div>
          </div>

          <div className="summary-group">
            <label>Content</label>
            <div className="summary-items">
              {tags.length > 0 && (
                <div className="summary-item">
                  <span className="item-label">Tags:</span>
                  <span className="item-value">{tags.length} tags</span>
                </div>
              )}
              {outcomes.length > 0 && (
                <div className="summary-item">
                  <span className="item-label">Learning Outcomes:</span>
                  <span className="item-value">{outcomes.length} outcomes</span>
                </div>
              )}
              {requirements.length > 0 && (
                <div className="summary-item">
                  <span className="item-label">Requirements:</span>
                  <span className="item-value">{requirements.length} requirements</span>
                </div>
              )}
              {faqCount > 0 && (
                <div className="summary-item">
                  <span className="item-label">FAQs:</span>
                  <span className="item-value">{faqCount} FAQs</span>
                </div>
              )}
              {courseType === 'web' || courseType === 'blended' ? (
                <div className="summary-item">
                  <span className="item-label">Modules:</span>
                  <span className="item-value">{modulesCount} modules</span>
                </div>
              ) : null}
              {courseType === 'ilt' && (
                <>
                  <div className="summary-item">
                    <span className="item-label">Offerings:</span>
                    <span className="item-value">{offeringsCount} offerings</span>
                  </div>
                  <div className="summary-item">
                    <span className="item-label">Sessions:</span>
                    <span className="item-value">{sessionsCount} sessions</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="summary-group">
            <label>Settings</label>
            <div className="summary-items">
              <div className="summary-item">
                <span className="item-label">Visibility:</span>
                <span className="item-value">
                  {visibilityType === 'public' ? 'Public / Organization-Wide' : 'Restricted Access'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Publish Button */}
      <div className="action-section">
        <Button
          variant="primary"
          size="large"
          onClick={onCreateCourse}
          disabled={isCreating || hasErrors}
          loading={isCreating}
          className="create-btn"
        >
          {isCreating
            ? (isUpdate ? 'Publishing Course...' : 'Creating Course...')
            : (isUpdate ? 'Publish Course' : 'Create Course')
          }
        </Button>
        <p className="action-note">
          {isUpdate
            ? 'Your course draft will be published and made available to learners.'
            : 'Once created, you can add sections, lessons, and publish your course.'
          }
        </p>
      </div>
    </div>
  );
};
